import React from 'react'
import { Icons } from '@pankod/refine-antd'
import { useCan, useCreate } from '@pankod/refine-core'

import { showErrorNotification } from '@resources/angkatan-ppg-management/utils'
import MenuItemPopconfirm from '../../MenuItemPopconfirm'
import { Highlighter } from '../../utils'
import { StatusTag } from '../StatusTag'

import type {
  TConfigurationMenuItems,
  TConfigurationSectionContext,
} from '../types'

export const useParticipantProfileItems = ({
  invalidateProgramDetail,
  isOwner,
  program,
  programId,
  search,
}: TConfigurationSectionContext): TConfigurationMenuItems => {
  const { mutateAsync: toggleModifyProfile } = useCreate()
  const { mutateAsync: toggleApprovalModifyProfile } = useCreate()
  const { data: canToggleModifyProfile } = useCan({
    action: 'field',
    resource: 'lms-program-management',
    params: { field: 'toggle-modify-profile' },
  })
  const modifyProfileActive = program?.modifyProfileActive
  const autoApproveActive = program?.autoApproveModifyProfileActive
  const canUpdate = canToggleModifyProfile?.can || isOwner

  return [
    {
      key: 'Ubah Profil (Primer)',
      type: 'group',
      label: (
        <div className="flex justify-between gap-2">
          <Highlighter search={search} text="Ubah Profil (Primer)" />
          <StatusTag
            active={!!modifyProfileActive}
            activeText="AKTIF"
            inactiveText="NONAKTIF"
          />
        </div>
      ),
      children: [
        {
          icon: modifyProfileActive ? (
            <Icons.StopOutlined />
          ) : (
            <Icons.FormOutlined />
          ),
          key: modifyProfileActive
            ? 'Nonaktifkan Ubah Profil (Primer)'
            : 'Aktifkan Ubah Profil (Primer)',
          danger: modifyProfileActive && canUpdate,
          disabled: !canUpdate,
          label: (
            <MenuItemPopconfirm
              title={`Apakah Anda ingin ${
                modifyProfileActive ? 'me-nonaktifkan' : 'mengaktifkan'
              } fitur ubah data profil (primer)?`}
              okText={modifyProfileActive ? 'Nonaktifkan' : 'Aktifkan'}
              cancelText="Batal"
              disabled={!canUpdate}
              okButtonProps={{ danger: modifyProfileActive }}
              onConfirm={() =>
                toggleModifyProfile(
                  {
                    resource: `programs/${programId}/modify-profile`,
                    dataProviderName: 'lms',
                    values: { active: !modifyProfileActive },
                    successNotification: {
                      message:
                        'Fitur ubah data profil (primer) berhasil dikonfigurasi ulang',
                      type: 'success',
                      description: 'Sukses',
                    },
                    errorNotification: (error) =>
                      showErrorNotification(
                        error,
                        'Terdapat gangguan saat mengubah konfigurasi ubah data profil (primer)',
                      ),
                  },
                  { onSuccess: invalidateProgramDetail },
                )
              }
            >
              <Highlighter
                search={search}
                text={
                  modifyProfileActive
                    ? 'Nonaktifkan Ubah Profil (Primer)'
                    : 'Aktifkan Ubah Profil (Primer)'
                }
              />
            </MenuItemPopconfirm>
          ),
        },
        {
          key: 'Auto-approve Pengajuan',
          type: 'group',
          label: (
            <div className="flex justify-between gap-2">
              <Highlighter search={search} text="Auto-approve Pengajuan" />
              <StatusTag
                active={!!autoApproveActive}
                activeText="AKTIF"
                inactiveText="NONAKTIF"
              />
            </div>
          ),
          children: [
            {
              icon: autoApproveActive ? (
                <Icons.StopOutlined />
              ) : (
                <Icons.CheckSquareOutlined />
              ),
              key: autoApproveActive
                ? 'Nonaktifkan Auto-approve Ubah Profil'
                : 'Aktifkan Auto-approve Ubah Profil',
              danger: autoApproveActive && canUpdate && modifyProfileActive,
              disabled: !modifyProfileActive || !canUpdate,
              label: (
                <MenuItemPopconfirm
                  title={`Apakah Anda ingin ${
                    autoApproveActive ? 'me-nonaktifkan' : 'mengaktifkan'
                  } fitur auto-approve ubah data profil (primer)?`}
                  okText={autoApproveActive ? 'Nonaktifkan' : 'Aktifkan'}
                  cancelText="Batal"
                  disabled={!modifyProfileActive || !canUpdate}
                  okButtonProps={{ danger: autoApproveActive }}
                  onConfirm={() =>
                    toggleApprovalModifyProfile(
                      {
                        resource: `programs/${programId}/auto-approve-modify-profile`,
                        dataProviderName: 'lms',
                        values: { active: !autoApproveActive },
                        successNotification: {
                          message:
                            'Fitur auto-approve ubah data profil (primer) berhasil dikonfigurasi ulang',
                          type: 'success',
                          description: 'Sukses',
                        },
                        errorNotification: (error) =>
                          showErrorNotification(
                            error,
                            'Terdapat gangguan saat mengubah konfigurasi auto-approve ubah data profil (primer)',
                          ),
                      },
                      { onSuccess: invalidateProgramDetail },
                    )
                  }
                >
                  <Highlighter
                    search={search}
                    text={
                      autoApproveActive
                        ? 'Nonaktifkan Auto-approve Ubah Profil'
                        : 'Aktifkan Auto-approve Ubah Profil'
                    }
                  />
                </MenuItemPopconfirm>
              ),
            },
          ],
        },
      ],
    },
  ]
}
